import { CubaREEUPCodeRuler } from '@dofleini/validator';
import { Button, Col, Form, Input, Row } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import FormBuilder from 'antd-form-builder';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useDeepCompareEffect from 'use-deep-compare-effect';

import FloatContainer, { TYPE_CONTAINER } from '@/components/FloatContainer';
import FormTitleSession from '@/components/FormTitleSession';
import InfoBox from '@/components/InfoBox';
import MunicipalitySelect from '@/components/MunicipalitySelect';
import ProvinceSelect from '@/components/ProvinceSelect';
import { useDialogsContext } from '@/contexts/DialogsContext';
import { useResponsive } from '@/contexts/ResponsiveContext';
import { DIALOG_STATE_PARTNER_NAMESPACE } from '@/modules/proposalRequest/contants/proposalRequest';
import { validations } from '@/utils/fields';

const ProposalRequestStatePartnerFormModal = ({ onConfirm }) => {
  const { t } = useTranslation('proposalRequest');
  const { isOpen, closeDialog, payload } = useDialogsContext(DIALOG_STATE_PARTNER_NAMESPACE);
  const { isMobile } = useResponsive();
  const [form] = Form.useForm();
  const forceUpdate = FormBuilder.useForceUpdate();
  const initValues = Object.assign({}, payload?.partner || {});

  const { address } = form.getFieldsValue();

  useEffect(() => {
    isOpen && form.resetFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const onCLose = useCallback(() => {
    closeDialog();
    form.resetFields();
  }, [closeDialog, form]);

  const handleSubmit = useCallback(async (values) => {
    await onConfirm(
      {
        ...values,
        representative: payload?.partner?.representative || undefined
      },
      payload?.isEdit,
      payload?.recordIndex
    );
    onCLose();
  }, [onConfirm, payload, onCLose]);

  useDeepCompareEffect(() => {
    if (!isEmpty(initValues)) {
      form.setFieldsValue(initValues);
    }
  }, [initValues]);

  return (
    <FloatContainer
      type={isMobile ? TYPE_CONTAINER.DRAWER : TYPE_CONTAINER.MODAL}
      visible={isOpen}
      title={null}
      closable={false}
      bodyStyle={{ padding: '0.1rem 1.5rem' }}
      onClose={onCLose}
      width={700}
      footer={[
        <Button className="mx-2" key="back" onClick={onCLose}>
          {t('cancel')}
        </Button>,
        <Button className="mx-2" key="submit" type="primary" onClick={() => form.submit()}>
          {t('confirm')}
        </Button>,
      ]}>
      <Form
        scrollToFirstError
        form={form}
        onFinish={handleSubmit}
        layout="vertical"
        onValuesChange={forceUpdate}
        initialValues={initValues}>
        <FormTitleSession
          translate={'proposalRequest'} title={'proposalRequest.form.partners.modal.title'}
          description={'proposalRequest.form.partners.modal.description'}/>
        <div>
          <Form.Item label={t('proposalRequest.form.partners.modal.entityDenomination')} name="entityDenomination"
            rules={[{ required: true }, validations.textLength()]}>
            <Input size={'large'} placeholder={t('defaultPlaceholder')}/>
          </Form.Item>
          <Form.Item label={t('proposalRequest.form.partners.modal.reeup')} name="reeup"
            rules={[{ required: true }, CubaREEUPCodeRuler]} extra={<InfoBox title="cubaREEUPCodeHelp"/>}>
            <Input size={'large'} placeholder={t('defaultPlaceholder')}/>
          </Form.Item>
          <Form.Item label={t('proposalRequest.form.partners.modal.integrationSubordination')}
            name="integrationSubordination"
            rules={[{ required: true }, validations.textLength()]}>
            <Input size={'large'} placeholder={t('defaultPlaceholder')}/>
          </Form.Item>
          <div>
            <Row gutter={20}>
              <Col span={12} xs={24} lg={12}>
                <Form.Item label={t('Provincia')} rules={[{ required: true }]} required name={['address', 'province']}>
                  <ProvinceSelect size={'large'} placeholder={t('selectPlaceholder')}/>
                </Form.Item>
              </Col>
              <Col span={12} xs={24} lg={12}>
                <Form.Item required label={t('municipality')} rules={[{ required: true }]}
                  name={['address', 'municipality']}>
                  <MunicipalitySelect province={address?.province} size={'large'} placeholder={t('selectPlaceholder')}/>
                </Form.Item>
              </Col>
            </Row>
            <Form.Item required label={t('address')} rules={[{ required: true }, validations.addressLength(), validations.addressValidator()]} name={['address', 'abstract']}>
              <TextArea maxLength={255} autoSize={{ minRows: 2, maxRows: 5 }} size={'large'}/>
            </Form.Item>
          </div>
        </div>
      </Form>
    </FloatContainer>);
};

ProposalRequestStatePartnerFormModal.propTypes = {
  onConfirm: PropTypes.func
};

ProposalRequestStatePartnerFormModal.defaultProps = {};

export default memo(ProposalRequestStatePartnerFormModal);
